import type { SharedSkillListParams, SkillListParams } from '../../lib/api/skills';
import type { MemberRunnerModelQueryParams } from '../../lib/api/memberRunnerModels';

export type PlanQueryParams = {
  search?: string;
  title?: string;
  status?: string;
  type?: string;
  priority?: string;
  assignedTo?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  pageSize?: number;
};

export type CommentQueryParams = {
  type?: string;
  order?: 'asc' | 'desc';
};

export type VersionDiffQueryParams = {
  against?: string;
  summaryOnly?: boolean;
};

export type CompletionReportQueryParams = {
  search?: string;
  createdByMemberId?: string;
  planId?: string;
  status?: string;
  reviewStatus?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  pageSize?: number;
};

export type CodeReviewQueryParams = {
  search?: string;
  createdByMemberId?: string;
  status?: string;
  targetType?: string;
  severity?: string;
  sourcePlanId?: string;
  sourceCompletionReportId?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  pageSize?: number;
};

export type ConventionQueryParams = {
  category?: string;
  scope?: string;
  archived?: 'ACTIVE' | 'ARCHIVED' | 'ALL';
  search?: string;
  createdByMemberId?: string;
  page?: number;
  pageSize?: number;
};

export type DocumentQueryParams = {
  q?: string;
  createdByMemberId?: string;
  tags?: string[];
  tagPrefix?: string;
  untagged?: boolean;
  favorite?: boolean;
  sharedByMe?: boolean;
  visibility?: 'PROJECT' | 'PRIVATE';
  archived?: 'ACTIVE' | 'ARCHIVED' | 'ALL';
  page?: number;
  pageSize?: number;
};

export type DocumentTagsQueryParams = {
  visibility?: 'PROJECT' | 'PRIVATE';
  archived?: 'ACTIVE' | 'ARCHIVED' | 'ALL';
  // 문서함 작성자 필터 값. 키에 포함되어 작성자를 바꾸면 태그 목록이 다시 조회된다.
  createdByMemberId?: string;
};

export type PostMortemQueryParams = {
  search?: string;
  createdByMemberId?: string;
  planId?: string;
  status?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  pageSize?: number;
};

export type CoActionQueryParams = {
  search?: string;
  createdByMemberId?: string;
  status?: string;
  visibility?: string;
  source?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  pageSize?: number;
};

export type FeedbackQueryParams = {
  search?: string;
  status?: string;
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
};

export type SystemMemberQueryParams = {
  search?: string;
  tier?: 'FREE' | 'PRO';
  signupType?: 'GOOGLE' | 'MICROSOFT' | 'GITHUB' | 'APPLE' | 'EMAIL';
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
  includeStats?: boolean;
};

export type SystemTeamQueryParams = {
  search?: string;
  plan?: 'PERSONAL_PRO' | 'TEAM_PRO' | 'PRO' | 'NONE';
  status?: 'ACTIVE' | 'TRIALING' | 'PAST_DUE' | 'CANCELLED' | 'NONE';
  page?: number;
  pageSize?: number;
};

export type RunnerModelQueryParams = {
  runnerType?: string;
  page?: number;
  pageSize?: number;
};

// projectIds 선택을 queryKey 세그먼트로 정규화한다(정렬 후 join). 비어있으면 빈 배열을 반환해
// 키에서 생략 → 무효화 측의 짧은 prefix와 호환된다.
const projectIdsKeyPart = (projectIds?: string[]): readonly [string] | readonly [] =>
  projectIds && projectIds.length > 0 ? ([[...projectIds].sort().join(',')] as const) : ([] as const);

export const queryKeys = {
  navigationBadges: {
    all: ['navigationBadges'] as const,
    project: (projectId: string) => ['navigationBadges', projectId] as const,
    detail: (projectId: string, memberId: string, permissions: readonly string[]) =>
      ['navigationBadges', projectId, memberId, [...new Set(permissions)].sort().join(',')] as const,
  },
  teams: {
    all: ['teams'] as const,
    members: (teamId: string) => ['teams', teamId, 'members'] as const,
  },
  member: {
    me: ['member', 'me'] as const,
  },
  members: {
    projectMembers: (projectId: string) => ['projectMembers', projectId] as const,
    projectAuthorOptions: (projectId: string) => ['projectMembers', projectId, 'authorOptions'] as const,
    teamMembers: (teamId: string) => ['teamMembers', teamId] as const,
  },
  projects: {
    byTeam: (teamId: string) => ['projects', teamId] as const,
    shareableByTeam: (teamId: string) => ['projects', teamId, 'shareable'] as const,
    byTeamWithArchived: (teamId: string, archived: 'active' | 'archived' | 'all') =>
      ['projects', teamId, 'archived', archived] as const,
    detail: (projectId: string) => ['projects', 'detail', projectId] as const,
    repositories: (projectId: string) => ['projects', projectId, 'repositories'] as const,
    /** 현재 사용자의 유효 permission. 역할 변경 뮤테이션은 이 키를 반드시 무효화한다. */
    access: (projectId: string) => ['projects', projectId, 'access'] as const,
    roles: (projectId: string) => ['projects', projectId, 'roles'] as const,
    permissionCatalog: (projectId: string) => ['projects', projectId, 'permission-catalog'] as const,
    repositoryBranches: (projectId: string, repositoryId: string) =>
      ['projects', projectId, 'repositories', repositoryId, 'branches'] as const,
  },
  sentry: {
    all: (projectId: string) => ['sentry', projectId] as const,
    connection: (projectId: string) => ['sentry', projectId, 'connection'] as const,
    organizations: (projectId: string) => ['sentry', projectId, 'organizations'] as const,
    projects: (projectId: string, organizationSlug: string) =>
      ['sentry', projectId, 'organizations', organizationSlug, 'projects'] as const,
  },
  activityDetails: {
    all: ['activityDetails'] as const,
    detail: (projectId: string, entityType: string, id: string, transport: 'graphql' | 'rest') =>
      ['activityDetails', projectId, entityType, id, transport] as const,
  },
  planInlineSummaries: {
    all: ['planInlineSummaries'] as const,
    detail: (projectId: string, planIds: string[]) =>
      ['planInlineSummaries', projectId, [...planIds].sort().join(',')] as const,
  },
  plans: {
    all: (projectId: string) => ['plans', projectId] as const,
    lists: (projectId: string) => ['plans', projectId, 'list'] as const,
    list: (projectId: string, params: PlanQueryParams = {}) => ['plans', projectId, 'list', params] as const,
    infinite: (projectId: string, params: Omit<PlanQueryParams, 'page' | 'pageSize'> = {}) =>
      ['plans', projectId, 'infinite', params] as const,
    detail: (projectId: string, id: string) => ['plans', projectId, 'detail', id] as const,
    fullDetail: (projectId: string, id: string) => ['plans', projectId, 'fullDetail', id] as const,
    sectionBody: (projectId: string, planId: string, sectionId: string) =>
      ['plans', projectId, 'body', planId, 'section', sectionId] as const,
    taskBody: (projectId: string, planId: string, taskId: string) =>
      ['plans', projectId, 'body', planId, 'task', taskId] as const,
    structuredBodies: (projectId: string, planId: string) => ['plans', projectId, 'body', planId, 'all'] as const,
    workflowRuns: (projectId: string, planId: string) => ['plans', projectId, 'workflowRuns', planId] as const,
    workflowRunGraph: (projectId: string, planId: string, workflowRunId: string) =>
      ['plans', projectId, 'workflowRuns', planId, 'graph', workflowRunId] as const,
    dependencies: (projectId: string, id: string) => ['plans', projectId, 'dependencies', id] as const,
    originIssues: (projectId: string, planId: string) => ['plans', projectId, 'originIssues', planId] as const,
  },
  agentConfigs: {
    all: (projectId: string) => ['agentConfigs', projectId] as const,
    detail: (projectId: string, id: string) => ['agentConfigs', projectId, 'detail', id] as const,
    convention: (projectId: string, id: string) => ['agentConfigs', projectId, 'convention', id] as const,
  },
  serviceTokens: {
    all: (projectId: string) => ['serviceTokens', projectId] as const,
  },
  daemonTriggers: {
    all: ['daemonTriggers'] as const,
    lists: ['daemonTriggers', 'list'] as const,
    list: (
      params: {
        status?: string;
        projectId?: string;
        agentConfigId?: string;
        targetDaemonId?: string;
        runnerType?: string;
        prompt?: string;
        planId?: string;
        limit?: number;
        page?: number;
        pageSize?: number;
      } = {},
    ) => ['daemonTriggers', 'list', params] as const,
    infinites: ['daemonTriggers', 'infinite'] as const,
    infinite: (
      params: {
        status?: string;
        projectId?: string;
        agentConfigId?: string;
        targetDaemonId?: string;
        runnerType?: string;
        prompt?: string;
      } = {},
    ) => ['daemonTriggers', 'infinite', params] as const,
    details: ['daemonTriggers', 'detail'] as const,
    detail: (id: string) => ['daemonTriggers', 'detail', id] as const,
    // 파라미터(agentConfigId/projectId)를 모르는 SSE 무효화에서 전체를 한 번에 칠 수 있는 prefix.
    activeWorktreesAll: ['daemonTriggers', 'activeWorktrees'] as const,
    worktreeLists: ['daemonTriggers', 'worktreeList'] as const,
    activeWorktrees: (agentConfigId: string) => ['daemonTriggers', 'activeWorktrees', agentConfigId] as const,
    resolvedSettings: (params: {
      agentConfigId?: string;
      projectId?: string;
      targetDaemonId?: string | null;
      runnerType?: string;
      model?: string | null;
      fastMode?: boolean;
      planMode?: boolean;
      useWorktree?: boolean;
    }) => ['daemonTriggers', 'resolveSettings', params] as const,
    worktreeList: (projectId: string, includeRemoved: boolean) =>
      ['daemonTriggers', 'worktreeList', projectId, includeRemoved] as const,
    discoveredWorktreesAll: ['daemonTriggers', 'discoveredWorktrees'] as const,
    discoveredWorktrees: (projectId: string) => ['daemonTriggers', 'discoveredWorktrees', projectId] as const,
    logs: (triggerId: string, params: { cursor?: string; limit?: number } = {}) =>
      ['daemonTriggers', triggerId, 'logs', params] as const,
    logStream: (triggerId: string) => ['daemonTriggers', triggerId, 'logStream'] as const,
    injectedConventions: (triggerId: string) => ['daemonTriggers', triggerId, 'injectedConventions'] as const,
  },
  attachments: {
    all: ['attachments'] as const,
    // 대상 일반화 키. daemonTrigger(targetId)는 ["attachments","daemonTrigger",id]로 기존 키와 동일하다.
    target: (targetType: string, targetId: string) => ['attachments', targetType, targetId] as const,
    daemonTrigger: (triggerId: string) => ['attachments', 'daemonTrigger', triggerId] as const,
  },
  daemons: {
    all: ['daemons'] as const,
    shares: (daemonId?: string) => ['daemons', 'shares', daemonId ?? 'all'] as const,
    shared: (projectId: string) => ['daemons', 'shared', projectId] as const,
    /// 공유 시 노출 대상으로 고를 수 있는 소유자 AgentConfig 후보.
    shareCandidates: (daemonId: string, projectId: string) =>
      ['daemons', 'shareCandidates', daemonId, projectId] as const,
  },
  comments: {
    all: (projectId: string) => ['comments', projectId] as const,
    list: (projectId: string, planId: string, params: CommentQueryParams = {}) =>
      ['comments', projectId, planId, 'list', params] as const,
    infinite: (projectId: string, planId: string, params: CommentQueryParams = {}) =>
      ['comments', projectId, planId, 'infinite', params] as const,
  },
  // 1-depth 답글. 플랜/문서 원댓글 공용(commentId 단위).
  commentReplies: {
    all: (projectId: string) => ['commentReplies', projectId] as const,
    list: (projectId: string, commentId: string, order: 'asc' | 'desc' = 'asc') =>
      ['commentReplies', projectId, commentId, 'list', order] as const,
  },
  completionReports: {
    reviewRecommendation: (projectId: string, id: string) =>
      ['completionReports', projectId, 'reviewRecommendation', id] as const,
    all: (projectId: string) => ['completionReports', projectId] as const,
    lists: (projectId: string) => ['completionReports', projectId, 'list'] as const,
    list: (projectId: string, params: CompletionReportQueryParams = {}) =>
      ['completionReports', projectId, 'list', params] as const,
    infinite: (projectId: string, params: Omit<CompletionReportQueryParams, 'page' | 'pageSize'> = {}) =>
      ['completionReports', projectId, 'infinite', params] as const,
    detail: (projectId: string, id: string) => ['completionReports', projectId, 'detail', id] as const,
  },
  codeReviews: {
    all: (projectId: string) => ['codeReviews', projectId] as const,
    lists: (projectId: string) => ['codeReviews', projectId, 'list'] as const,
    list: (projectId: string, params: CodeReviewQueryParams = {}) =>
      ['codeReviews', projectId, 'list', params] as const,
    infinite: (projectId: string, params: Omit<CodeReviewQueryParams, 'page' | 'pageSize'> = {}) =>
      ['codeReviews', projectId, 'infinite', params] as const,
    detail: (projectId: string, id: string) => ['codeReviews', projectId, 'detail', id] as const,
    findings: (projectId: string, id: string, params: { page?: number; pageSize?: number } = {}) =>
      ['codeReviews', projectId, 'detail', id, 'findings', params] as const,
    findingsInfinite: (projectId: string, id: string) =>
      ['codeReviews', projectId, 'detail', id, 'findings', 'infinite'] as const,
  },
  postMortems: {
    all: (projectId: string) => ['postMortems', projectId] as const,
    lists: (projectId: string) => ['postMortems', projectId, 'list'] as const,
    list: (projectId: string, params: PostMortemQueryParams = {}) =>
      ['postMortems', projectId, 'list', params] as const,
    infinite: (projectId: string, params: Omit<PostMortemQueryParams, 'page' | 'pageSize'> = {}) =>
      ['postMortems', projectId, 'infinite', params] as const,
    detail: (projectId: string, id: string) => ['postMortems', projectId, 'detail', id] as const,
  },
  coActions: {
    all: (projectId: string) => ['coActions', projectId] as const,
    lists: (projectId: string) => ['coActions', projectId, 'list'] as const,
    list: (projectId: string, params: CoActionQueryParams = {}) => ['coActions', projectId, 'list', params] as const,
    infinite: (projectId: string, params: Omit<CoActionQueryParams, 'page' | 'pageSize'> = {}) =>
      ['coActions', projectId, 'infinite', params] as const,
    detail: (projectId: string, id: string) => ['coActions', projectId, 'detail', id] as const,
    takeaways: (projectId: string, coActionId: string) => ['coActions', projectId, 'takeaways', coActionId] as const,
    histories: (projectId: string, coActionId: string) => ['coActions', projectId, 'histories', coActionId] as const,
    triggers: (projectId: string, coActionId: string) => ['coActions', projectId, 'triggers', coActionId] as const,
  },
  dashboard: {
    all: (projectId: string) => ['dashboard', projectId] as const,
    summary: (projectId: string) => ['dashboard', projectId, 'summary'] as const,
    summaryV2: (projectId: string) => ['dashboard', projectId, 'summaryV2'] as const,
  },
  me: {
    all: ['me'] as const,
    /** 내가 받은 대기 중 초대 목록. */
    invitations: ['me', 'invitations'] as const,
    /// 홈 부트스트랩(me.home(...)) 전체를 필터 조합과 무관하게 무효화하기 위한 prefix.
    homeBootstrapPrefix: ['me', 'home'] as const,
    home: (
      filter: {
        limit?: number;
        entity?: string[];
        author?: string;
        planStatus?: string[];
        reportStatus?: string[];
        postMortemStatus?: string[];
        coActionStatus?: string[];
        codeReviewStatus?: string[];
        projectIds?: string[];
        days?: number;
        tzOffsetMinutes?: number;
      } = {},
    ) => {
      const joinSorted = (xs: string[] | undefined) => (xs && xs.length > 0 ? [...xs].sort().join(',') : '');
      return [
        'me',
        'home',
        {
          limit: filter.limit ?? 0,
          entity: joinSorted(filter.entity),
          author: filter.author ?? '',
          planStatus: joinSorted(filter.planStatus),
          reportStatus: joinSorted(filter.reportStatus),
          postMortemStatus: joinSorted(filter.postMortemStatus),
          coActionStatus: joinSorted(filter.coActionStatus),
          codeReviewStatus: joinSorted(filter.codeReviewStatus),
          projectIds: joinSorted(filter.projectIds),
          days: filter.days ?? 0,
          tzOffsetMinutes: filter.tzOffsetMinutes ?? 0,
        },
      ] as const;
    },
    // 홈 피드 전체 프리픽스(커서/필터 변형 모두 매칭). SSE 도메인 이벤트 무효화에서
    // me.all(히트맵·프로젝트목록까지 포함) 대신 피드만 좁혀 무효화할 때 사용한다.
    feedAll: ['me', 'feed'] as const,
    feed: (
      cursor: string | null,
      filter: {
        entity?: string[];
        author?: string;
        planStatus?: string[];
        reportStatus?: string[];
        postMortemStatus?: string[];
        coActionStatus?: string[];
        codeReviewStatus?: string[];
        projectIds?: string[];
        limit?: number;
      } = {},
    ) => {
      const joinSorted = (xs: string[] | undefined) => (xs && xs.length > 0 ? [...xs].sort().join(',') : '');
      return [
        'me',
        'feed',
        cursor ?? 'head',
        {
          entity: joinSorted(filter.entity),
          author: filter.author ?? '',
          planStatus: joinSorted(filter.planStatus),
          reportStatus: joinSorted(filter.reportStatus),
          postMortemStatus: joinSorted(filter.postMortemStatus),
          coActionStatus: joinSorted(filter.coActionStatus),
          codeReviewStatus: joinSorted(filter.codeReviewStatus),
          projectIds: joinSorted(filter.projectIds),
          limit: filter.limit ?? 0,
        },
      ] as const;
    },
    // projectIds는 비어있으면 키에서 생략한다. 그래야 무효화 측(projectIds를 모르는 SSE 핸들러)이
    // 짧은 prefix로 호출해도 selection이 붙은 더 긴 키까지 prefix 매칭으로 무효화된다.
    actionRequired: (projectIds?: string[]) => ['me', 'actionRequired', ...projectIdsKeyPart(projectIds)] as const,
    activityHeatmap: (days: number, tzOffsetMinutes: number, projectIds?: string[]) =>
      ['me', 'activityHeatmap', days, tzOffsetMinutes, ...projectIdsKeyPart(projectIds)] as const,
    recentRuns: ['me', 'recentRuns'] as const,
    recentRunsByGroup: (group: 'active' | 'past' | 'all', projectIds?: string[], opts: { isFavorite?: boolean } = {}) =>
      ['me', 'recentRuns', group, ...projectIdsKeyPart(projectIds), ...(opts.isFavorite ? ['favorite'] : [])] as const,
    projects: ['me', 'projects'] as const,
  },
  openPullRequests: {
    all: ['openPullRequests'] as const,
    list: (projectIds?: string[], limit = 20) =>
      ['openPullRequests', ...projectIdsKeyPart(projectIds ? [...new Set(projectIds)] : undefined), { limit }] as const,
  },
  feedbacks: {
    all: ['feedbacks'] as const,
    list: (params: FeedbackQueryParams = {}) => ['feedbacks', 'list', params] as const,
    infinite: (params: Omit<FeedbackQueryParams, 'page' | 'pageSize'> = {}) =>
      ['feedbacks', 'infinite', params] as const,
  },
  systemMembers: {
    all: ['systemMembers'] as const,
    list: (params: SystemMemberQueryParams = {}) => ['systemMembers', 'list', params] as const,
    infinite: (params: Omit<SystemMemberQueryParams, 'page' | 'pageSize'> = {}) =>
      ['systemMembers', 'infinite', params] as const,
  },
  systemTeams: {
    all: ['systemTeams'] as const,
    list: (params: SystemTeamQueryParams = {}) => ['systemTeams', 'list', params] as const,
    infinite: (params: Omit<SystemTeamQueryParams, 'page' | 'pageSize'> = {}) =>
      ['systemTeams', 'infinite', params] as const,
  },
  runnerModels: {
    all: ['runnerModels'] as const,
    public: ['runnerModels', 'public'] as const,
    merged: (memberId: string) => ['runnerModels', 'merged', memberId] as const,
    catalogs: (projectId: string) => ['runnerModels', 'catalogs', projectId] as const,
    forDaemon: (projectId: string, daemonId: string) => ['runnerModels', 'catalogs', projectId, daemonId] as const,
    systemList: (params: RunnerModelQueryParams = {}) => ['runnerModels', 'system', 'list', params] as const,
    systemInfinite: (params: Omit<RunnerModelQueryParams, 'page' | 'pageSize'> = {}) =>
      ['runnerModels', 'system', 'infinite', params] as const,
  },
  memberRunnerModels: {
    all: ['memberRunnerModels'] as const,
    list: (params: MemberRunnerModelQueryParams = {}) => ['memberRunnerModels', 'list', params] as const,
  },
  skills: {
    all: (projectId: string) => ['skills', projectId] as const,
    shares: (projectId: string, id: string) => ['skills', projectId, 'shares', id] as const,
    installs: (projectId: string, id: string, page: number) => ['skills', projectId, 'installs', id, page] as const,
    lists: (projectId: string) => ['skills', projectId, 'list'] as const,
    list: (projectId: string, params: SkillListParams = {}) => ['skills', projectId, 'list', params] as const,
    infinite: (projectId: string, params: SkillListParams = {}) => ['skills', projectId, 'infinite', params] as const,
    detail: (projectId: string, id: string) => ['skills', projectId, 'detail', id] as const,
    package: (projectId: string, id: string) => ['skills', projectId, 'package', id] as const,
    history: (projectId: string, id: string) => ['skills', projectId, 'history', id] as const,
    historyEntry: (projectId: string, id: string, historyId: string) =>
      ['skills', projectId, 'history', id, historyId] as const,
    versionDiff: (projectId: string, id: string, historyId: string, query: VersionDiffQueryParams = {}) =>
      ['skills', projectId, 'history', id, historyId, 'diff', query] as const,
    usageSummary: (projectId: string, params: { from?: string; to?: string } = {}) =>
      ['skills', projectId, 'usageSummary', params] as const,
    publicShare: (token: string) => ['skills', 'publicShare', token] as const,
    sharedList: (projectId: string, params: SharedSkillListParams = {}) =>
      ['skills', projectId, 'shared', 'list', params] as const,
    sharedInfinite: (projectId: string, params: Omit<SharedSkillListParams, 'page' | 'pageSize'> = {}) =>
      ['skills', projectId, 'shared', 'infinite', params] as const,
    sharedDetail: (projectId: string, shareId: string) => ['skills', projectId, 'shared', 'detail', shareId] as const,
  },
  conventions: {
    all: (projectId: string) => ['conventions', projectId] as const,
    lists: (projectId: string) => ['conventions', projectId, 'list'] as const,
    list: (projectId: string, params: ConventionQueryParams = {}) =>
      ['conventions', projectId, 'list', params] as const,
    infinite: (projectId: string, params: Omit<ConventionQueryParams, 'page' | 'pageSize'> = {}) =>
      ['conventions', projectId, 'infinite', params] as const,
    detail: (projectId: string, id: string) => ['conventions', projectId, 'detail', id] as const,
    history: (projectId: string, id: string) => ['conventions', projectId, 'history', id] as const,
    historyDetail: (projectId: string, conventionId: string, historyId: string) =>
      ['conventions', projectId, 'history', conventionId, historyId] as const,
    versionDiff: (projectId: string, conventionId: string, historyId: string, query: VersionDiffQueryParams = {}) =>
      ['conventions', projectId, 'history', conventionId, historyId, 'diff', query] as const,
    changes: (projectId: string) => ['conventions', projectId, 'changes'] as const,
    usageSummary: (projectId: string, params: { from?: string; to?: string } = {}) =>
      ['conventions', projectId, 'usageSummary', params] as const,
  },
  documents: {
    all: (projectId: string) => ['documents', projectId] as const,
    lists: (projectId: string) => ['documents', projectId, 'list'] as const,
    list: (projectId: string, params: DocumentQueryParams = {}) => ['documents', projectId, 'list', params] as const,
    infinites: (projectId: string) => ['documents', projectId, 'infinite'] as const,
    infinite: (projectId: string, params: Omit<DocumentQueryParams, 'page' | 'pageSize'> = {}) =>
      ['documents', projectId, 'infinite', params] as const,
    tags: (projectId: string, params: DocumentTagsQueryParams = {}) =>
      ['documents', projectId, 'tags', params] as const,
    tagSuggestions: (projectId: string, params: DocumentTagsQueryParams = {}) =>
      ['documents', projectId, 'tagSuggestions', params] as const,
    detail: (projectId: string, id: string) => ['documents', projectId, 'detail', id] as const,
    plans: (projectId: string, documentId: string) => ['documents', projectId, 'detail', documentId, 'plans'] as const,
    share: (projectId: string, id: string) => ['documents', projectId, 'detail', id, 'share'] as const,
    accessGrants: (projectId: string, id: string) => ['documents', projectId, 'detail', id, 'accessGrants'] as const,
    publicShare: (token: string) => ['documents', 'publicShare', token] as const,
    revisions: (projectId: string, documentId: string) => ['documents', projectId, 'revisions', documentId] as const,
    revisionDetail: (projectId: string, documentId: string, revisionId: string) =>
      ['documents', projectId, 'revisions', documentId, revisionId] as const,
    versionDiff: (projectId: string, documentId: string, revisionId: string, query: VersionDiffQueryParams = {}) =>
      ['documents', projectId, 'revisions', documentId, revisionId, 'diff', query] as const,
  },
  documentComments: {
    all: (projectId: string, documentId: string) => ['documentComments', projectId, documentId] as const,
    list: (projectId: string, documentId: string, order: 'asc' | 'desc' = 'desc') =>
      ['documentComments', projectId, documentId, 'list', order] as const,
  },
  findingComments: {
    all: (projectId: string, findingId: string) => ['findingComments', projectId, findingId] as const,
    list: (projectId: string, findingId: string) => ['findingComments', projectId, findingId, 'list'] as const,
    infinite: (projectId: string, findingId: string) => ['findingComments', projectId, findingId, 'infinite'] as const,
  },
  taskComments: {
    all: (projectId: string, taskId: string) => ['taskComments', projectId, taskId] as const,
    list: (projectId: string, taskId: string) => ['taskComments', projectId, taskId, 'list'] as const,
    infinite: (projectId: string, taskId: string) => ['taskComments', projectId, taskId, 'infinite'] as const,
  },
  notifications: {
    all: (projectId: string) => ['notifications', projectId] as const,
    lists: (projectId: string) => ['notifications', projectId, 'list'] as const,
    list: (projectId: string, params: { page?: number; limit?: number; status?: string; type?: string } = {}) =>
      ['notifications', projectId, 'list', params] as const,
    infinite: (projectId: string, params: { status?: string; type?: string } = {}) =>
      ['notifications', projectId, 'infinite', params] as const,
    unreadCount: (projectId: string) => ['notifications', projectId, 'unreadCount'] as const,
    unified: {
      all: ['notifications', 'unified'] as const,
      list: (params: { page?: number; limit?: number; status?: string; type?: string } = {}) =>
        ['notifications', 'unified', 'list', params] as const,
      infinite: (params: { status?: string; type?: string } = {}) =>
        ['notifications', 'unified', 'infinite', params] as const,
      detail: (id: string) => ['notifications', 'unified', 'detail', id] as const,
      unreadCount: ['notifications', 'unified', 'unreadCount'] as const,
    },
  },
  heartbeat: {
    all: (projectId: string) => ['heartbeat', projectId] as const,
  },
  /**
   * 엔티티 링크 접근 요청. 요청자 스코프와 심사자 스코프는 같은 요청이라도 응답 내용이 달라
   * (심사자만 요청자 정보·심사 메모를 본다) 캐시를 분리한다.
   */
  entityAccessRequests: {
    all: ['entityAccessRequests'] as const,
    mine: (params: { status?: string; page?: number; pageSize?: number } = {}) =>
      ['entityAccessRequests', 'mine', params] as const,
    reviewable: (params: { status?: string; page?: number; pageSize?: number } = {}) =>
      ['entityAccessRequests', 'reviewable', params] as const,
    detail: (requestId: string) => ['entityAccessRequests', 'detail', requestId] as const,
  },
  runnerRequestBootstrap: {
    root: ['runnerRequestBootstrap'] as const,
    all: (projectId: string) => ['runnerRequestBootstrap', projectId] as const,
  },
  push: {
    all: ['push'] as const,
    subscriptionsAll: ['push', 'subscriptions'] as const,
    subscriptions: (endpoint: string) => ['push', 'subscriptions', endpoint] as const,
    preferences: (projectId: string) => ['push', 'preferences', projectId] as const,
    subscriptionStatus: (projectId: string, endpoint: string) =>
      ['push', 'subscriptionStatus', projectId, endpoint] as const,
  },
  notificationSettings: {
    all: ['notificationSettings'] as const,
  },
  search: {
    all: (projectId: string) => ['search', projectId] as const,
    meta: (projectId: string) => ['search', projectId, 'meta'] as const,
    query: (projectId: string, params: { q: string; types?: string[]; limit?: number }) =>
      ['search', projectId, 'query', params] as const,
  },
  searchReindex: {
    all: ['searchReindex'] as const,
    status: ['searchReindex', 'status'] as const,
  },
  promptPresets: {
    all: (projectId: string) => ['promptPresets', projectId] as const,
    lists: (projectId: string) => ['promptPresets', projectId, 'list'] as const,
    list: (projectId: string, params: { page?: number; pageSize?: number } = {}) =>
      ['promptPresets', projectId, 'list', params] as const,
  },
  runnerSchedules: {
    all: (projectId: string) => ['runnerSchedules', projectId] as const,
    list: (projectId: string, page: number) => ['runnerSchedules', projectId, 'list', page] as const,
    detail: (projectId: string, id: string) => ['runnerSchedules', projectId, 'detail', id] as const,
    runs: (projectId: string, id: string, page: number, pageSize = 10) =>
      ['runnerSchedules', projectId, 'runs', id, page, pageSize] as const,
  },
  runnerDispatchPolicies: {
    all: (projectId: string) => ['runnerDispatchPolicies', projectId] as const,
    list: (projectId: string, params: { entityType?: string; entityState?: string; entityId?: string } = {}) =>
      ['runnerDispatchPolicies', projectId, 'list', params] as const,
  },
  billing: {
    all: ['billing'] as const,
    subscription: ['billing', 'subscription'] as const,
    teamSubscription: (teamId: string) => ['billing', 'subscription', 'team', teamId] as const,
    payments: (params: { page?: number; limit?: number; teamId?: string | null } = {}) =>
      ['billing', 'payments', params] as const,
    providers: ['billing', 'providers'] as const,
  },
  systemConfig: {
    all: ['systemConfig'] as const,
    list: ['systemConfig', 'list'] as const,
    catalog: ['systemConfig', 'catalog'] as const,
  },
  deployDiagnostics: {
    all: ['deployDiagnostics'] as const,
  },
  sessions: {
    all: ['sessions'] as const,
    list: ['sessions', 'list'] as const,
  },
  systemUsage: {
    all: ['systemUsage'] as const,
    timeseries: (params: { from?: string; to?: string; dimension: 'endpointGroup' | 'total' }) =>
      ['systemUsage', 'timeseries', params] as const,
    clients: (params: { from?: string; to?: string }) => ['systemUsage', 'clients', params] as const,
    sse: (params: { from?: string; to?: string }) => ['systemUsage', 'sse', params] as const,
    sseActive: () => ['systemUsage', 'sseActive'] as const,
    activeRunners: () => ['systemUsage', 'activeRunners'] as const,
  },
} as const;
